/**
 * ehr#729 (SKILLS_REHAB_NOTE_10-30-V7) and ehr#732 (MEDICATION_MANAGEMENT_NOTE_10-30-V5): what a
 * Skills / rehab note and a Medication management note have of their own on the note engine
 * (V141). Both kinds were already in the EHR (their layouts, Last session of the same kind, H2014
 * on open for Skills / rehab); this adds what the 10-30 files give them. Pure, so the editor, Note
 * settings and the tests read the same rules. Tables are by kind, so another Other note can join.
 */
import { cardsHiddenFor, type QuickPrefs, type QuickSideCard } from '@ehr/shared';
import type { RailCardKey } from './collateralNote.js';

export const SKILLS_KIND = 'Skills / rehab';
export const MED_KIND = 'Medication management';

/**
 * Skills / Rehab Note V4 (Jay 8a, SKILLS_PHRASE_OPS): skills phrases in What happened and Next
 * step, in place of the therapy ones, by the box's body key.
 */
export const SKILLS_PHRASES: Readonly<Record<string, readonly string[]>> = {
  subjective: [
    'Practiced __ with the client at __.',
    'Client completed __ on their own.',
    'Client needed __ prompts to __.',
    'Showed the client how to __, then the client tried it.',
  ],
  plan: [
    'Next practice: __ on __.',
    'Use fewer prompts for __ next time.',
    'Client will practice __ before the next visit.',
  ],
};

/** The stock pills a kind has of its own under a box; undefined: the engine's usual ones. */
export function kindPhrasesFor(kind: string, key: string): readonly string[] | undefined {
  if (kind === SKILLS_KIND) return SKILLS_PHRASES[key] ?? [];
  return undefined;
}

/**
 * Skills V7 V3, Medication V5 (V1, V2): Session type is a read-only box that names the kind (no
 * dropdown), as on a discharge summary (ehr#708).
 */
const READ_ONLY_TYPE_KINDS: readonly string[] = ['Discharge summary', SKILLS_KIND, MED_KIND];
export const sessionTypeReadOnly = (kind: string): boolean => READ_ONLY_TYPE_KINDS.includes(kind);

/**
 * The rail cards (Skills V7 Notes drawer V1; Medication V5, whose file has no Quick add, Measures
 * or My changes card): Generate Notes and Last session only, so no Quick add item goes on a new
 * note either. The editor reads it through collateralNote's railCardOn, which every rail check
 * already calls.
 */
const KIND_RAIL: Readonly<Record<string, readonly RailCardKey[]>> = {
  [SKILLS_KIND]: ['gen', 'last'],
  [MED_KIND]: ['gen', 'last'],
};
export function kindRailCardOn(kind: string, card: RailCardKey): boolean {
  const cards = KIND_RAIL[kind];
  return !cards || cards.includes(card);
}

/**
 * Note settings for these kinds. Skills / rehab (V7 V3) has one Note tab: no Quick add or
 * Interventions tab, so no tab strip. Medication management (V5, V4 21 A) has Note, Helpers and
 * (admins) Interventions: Helpers holds only Show Last session and Show Generate Notes.
 * undefined: the usual tabs.
 */
export interface KindSettings {
  /** The Quick add tab's place: its title and the rail cards it switches; null: no such tab. */
  side: { title: string; cards: readonly QuickSideCard[] } | null;
  /** Whether the Interventions tab is offered (to admins). */
  interventions: boolean;
}
const KIND_SETTINGS: Readonly<Record<string, KindSettings>> = {
  [SKILLS_KIND]: { side: null, interventions: false },
  [MED_KIND]: { side: { title: 'Helpers', cards: ['last', 'gen'] }, interventions: true },
};
export const kindSettingsOf = (kind: string): KindSettings | undefined => KIND_SETTINGS[kind];

/**
 * V7 V3 (Jay 3a): the kinds whose default billing code is set in Note settings > Practice note
 * settings ("Skills / rehab default code"), for admins. It is the practice's Service note default
 * code, the same setting as Data & settings.
 */
export const defaultCodeKindOf = (kind: string): string | null =>
  kind === SKILLS_KIND ? SKILLS_KIND : null;

/**
 * Medication V5 (V4 bug fix): the codes a kind's Billing code list offers, in place of every
 * Other note code. A Medication management note: the E/M codes 99212, 99213 and 99214 only.
 */
const KIND_CODES: Readonly<Record<string, readonly string[]>> = {
  [MED_KIND]: ['99212', '99213', '99214'],
};
export function kindCodeList<T extends readonly [string, string]>(
  kind: string,
  list: readonly T[],
): readonly T[] {
  const own = KIND_CODES[kind];
  return own ? list.filter(([c]) => own.includes(c)) : list;
}

/**
 * Medication V5 (V2 13 b): nothing is generated when the note opens, even with rough notes saved;
 * the provider types or dictates, then clicks Apply to note.
 */
const NO_OPEN_GENERATE_KINDS: readonly string[] = [MED_KIND];
export const generatesOnOpen = (kind: string): boolean => !NO_OPEN_GENERATE_KINDS.includes(kind);

/**
 * Medication V5 Helpers (V4 21 A): what Save for this client / Save for all clients stores. Only
 * the offered cards' Show switches change; the other cards' switches and the Quick add lists stay
 * as they are. This client: the switches become the client's own (the client keeps the list it
 * has now). All clients: they become the all-clients ones and this client follows them again.
 */
export function helpersSaveShape(
  d: QuickPrefs,
  o: {
    to: 'client' | 'all';
    contactId: string;
    offered: readonly QuickSideCard[];
    /** The offered cards switched Off. */
    off: readonly QuickSideCard[];
  },
): QuickPrefs {
  const merge = (prev: readonly QuickSideCard[]) => [
    ...prev.filter((k) => !o.offered.includes(k)),
    ...o.off.filter((k) => o.offered.includes(k)),
  ];
  const { [o.contactId]: _own, ...otherCards } = d.clientCards ?? {};
  if (o.to === 'all')
    return { ...d, cardsHidden: merge(d.cardsHidden ?? []), clientCards: otherCards };
  return {
    ...d,
    clients: { ...d.clients, [o.contactId]: d.clients[o.contactId] ?? d.onNote },
    clientCards: { ...otherCards, [o.contactId]: merge(cardsHiddenFor(d, o.contactId)) },
  };
}
